import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Award, Briefcase, Building2, DollarSign, FileEdit, FileText, GitBranch, Loader2, Plus, Search, Shield, Target, Truck, Users } from "lucide-react";
import { API, TOOLS } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const DashboardPage = () => {
  const [stats, setStats] = useState({ total_reports: 0, total_documents: 0, total_timelines: 0, average_confidence: 0 });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, reportsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/reports`)
        ]);
        setStats(statsRes.data);
        setReports(reportsRes.data.slice(0, 5));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Dashboard</h1>
            <p className="text-muted-foreground">Your legal intelligence overview</p>
          </div>
          <Link to="/tools/analytics/new">
            <Button className="rounded-xl gap-2">
              <Plus className="h-4 w-4" /> New Analysis
            </Button>
          </Link>
        </div>
        
        {/* Stats */}
        <div className="stats-grid mb-8">
          {[
            { label: "Matter Reports", value: stats.total_reports, icon: FileText, color: "text-blue-600" },
            { label: "Documents Generated", value: stats.total_documents, icon: FileEdit, color: "text-violet-600" },
            { label: "Case Timelines", value: stats.total_timelines, icon: GitBranch, color: "text-cyan-600" },
            { label: "Avg Confidence", value: stats.average_confidence, icon: Target, color: "text-emerald-600" }
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className={`stat-card ${color}`}>
              <Icon className="h-5 w-5 mb-3 opacity-80" />
              <p className="text-sm text-muted-foreground mb-1">{label}</p>
              <p className="metric-value">{value}</p>
            </div>
          ))}
        </div>
        
        {/* Quick Tools */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Quick Access</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TOOLS.slice(0, 3).map(tool => (
              <Link to={`/tools/${tool.id}`} key={tool.id}>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${tool.color} flex items-center justify-center`}>
                      <tool.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium">{tool.name}</h3>
                      <p className="text-xs text-muted-foreground">{tool.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
        
        {/* Recent Reports */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Reports</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No reports yet</p>
                <Link to="/tools/analytics/new">
                  <Button variant="outline" className="mt-3">Create First Report</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map(report => (
                  <Link to={`/reports/${report.id}`} key={report.id}>
                    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">{report.title}</h4>
                          <p className="text-xs text-muted-foreground">
                            {new Date(report.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline">Confidence: {report.result?.scores?.data_confidence || 0}</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Service Networks Quick Access */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Service Networks
              <Badge className="bg-gradient-to-r from-slate-700 to-slate-900 ml-2">WHITE LABEL</Badge>
            </CardTitle>
            <CardDescription>Revenue-generating staffing, investigations, and professional services</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-3">
              {[
                { id: "services-hub", name: "Services Hub", icon: Building2, color: "from-slate-600 to-slate-800" },
                { id: "attorney-network", name: "Attorney Network", icon: Briefcase, color: "from-blue-600 to-blue-800" },
                { id: "investigation-services", name: "Investigations", icon: Search, color: "from-red-600 to-red-800" },
                { id: "insurance-analytics", name: "Insurance Analytics", icon: Shield, color: "from-emerald-600 to-emerald-800" },
                { id: "paralegal-network", name: "Paralegals", icon: Users, color: "from-cyan-600 to-cyan-800" },
                { id: "expert-network", name: "Expert Witnesses", icon: Award, color: "from-purple-600 to-purple-800" },
                { id: "legal-financing", name: "Legal Financing", icon: DollarSign, color: "from-green-600 to-green-800" },
                { id: "courier-services", name: "Courier", icon: Truck, color: "from-amber-600 to-amber-800" }
              ].map(svc => (
                <Link key={svc.id} to={`/tools/${svc.id}`}>
                  <div className="p-3 rounded-lg border hover:shadow-md transition-all flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${svc.color} text-white`}>
                      <svc.icon className="h-4 w-4" />
                    </div>
                    <span className="text-sm font-medium">{svc.name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </main>
  );
};


export default DashboardPage;
